/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Overlaying repeated sweeps on ONE set of axes.
 *
 * WHY. A sweep answers "how does Y respond to X"; the question right after it is
 * always "and what if it were hotter / denser / a different ligand?". Until now a
 * re-run produced a SECOND chart card, so comparing two runs meant eyeballing two
 * pictures with independently auto-scaled axes — which is exactly the comparison a
 * chart is supposed to make for you. This module folds the previous run into the
 * new attachment as an extra series, so "repeat it at 600 K" draws the 600 K curve
 * next to the 300 K one.
 *
 * Pure: it takes the previous attachment and the new one and returns the merged
 * attachment. The impure part — finding the previous sweep in the chat log — lives
 * in chatAttachments.ts. Tested by `npm run test:sweepoverlay`.
 */

import type { SweepChartAttachment, SweepSeries } from '../types.ts';
import { isOverlayableSweep } from './sweepRegistry.ts';

// How many EARLIER runs stay on the chart. Four curves plus the current one is
// already at the limit of what a small inline chart can distinguish; beyond that
// the oldest drops off rather than the plot turning into spaghetti.
export const MAX_OVERLAY_SERIES = 4;

/**
 * A run's series label. `conditions` is the string the sweep already writes to
 * describe what was held fixed ("T = 300 K · N = 200 atoms"), which is precisely
 * what distinguishes one run from another — so it becomes the legend entry, with
 * the run number as a fallback when a sweep records no conditions.
 */
export const seriesLabelFor = (att: SweepChartAttachment, runNumber: number): string =>
  att.conditions?.trim() ? att.conditions.trim() : `run ${runNumber}`;

// Axis titles renamed after charts carrying the old wording were already saved in
// chat history. Mapped at render and in the overlay check, so old charts read the
// new wording and still overlay with new runs.
const RENAMED_AXIS_LABELS: Record<string, string> = {
  // Seeded growth: nothing is melted when it starts from liquid over a pinned seed,
  // and the long title overflowed the 220 px chart's y axis.
  'Crystalline fraction of the melted half (%)': 'Crystallized (%)',
  'Crystalline fraction above the seed (%)': 'Crystallized (%)',
};
export const currentAxisLabel = (label: string): string => RENAMED_AXIS_LABELS[label] ?? label;

/** Two runs belong on one chart only if they measure the same thing the same way. */
export const canOverlay = (prev: SweepChartAttachment, next: SweepChartAttachment): boolean =>
  prev.sweepKind === next.sweepKind &&
  !prev.categorical &&
  !next.categorical &&
  isOverlayableSweep(next.sweepKind) &&
  currentAxisLabel(prev.xLabel) === currentAxisLabel(next.xLabel) &&
  currentAxisLabel(prev.yLabel) === currentAxisLabel(next.yLabel);

/**
 * Fold `prev` (and whatever it was already carrying) into `next` as earlier
 * series, oldest first. Returns `next` unchanged when the two runs don't belong on
 * the same axes — a caller can always call this and let it decide.
 *
 * The CURRENT run stays in `points`/`fit`; only earlier runs live in `series`. That
 * keeps every attachment persisted before overlays existed valid, and keeps the
 * renderer's "draw the latest on top" rule trivial.
 */
export const overlaySweeps = (
  prev: SweepChartAttachment | null | undefined,
  next: SweepChartAttachment,
  maxSeries = MAX_OVERLAY_SERIES,
): SweepChartAttachment => {
  if (!prev || !canOverlay(prev, next)) return next;
  // Everything prev was already showing, plus prev's own current run.
  const earlier: SweepSeries[] = [
    ...(prev.series ?? []),
    { label: seriesLabelFor(prev, (prev.series?.length ?? 0) + 1), points: prev.points, fit: prev.fit },
  ];
  // Drop the OLDEST when we run out of room — the recent comparison is the one
  // the user is making.
  const kept = maxSeries > 0 ? earlier.slice(-maxSeries) : [];
  if (!kept.length) return next;
  // NOTE: `conditions` is left exactly as the sweep wrote it. It describes THIS run,
  // and it is also what the NEXT merge turns into this run's legend label — so
  // appending a run counter here would compound into the label on every repeat
  // ("T = 600 K · run 2 · run 3"). The run number is already visible as the number
  // of legend entries.
  return { ...next, series: kept };
};

// A number keeps its unit ("1000 K", "15 Å", "2%") so trimming never strands one half.
const LABEL_TOKEN = /-?\d[\d.,]*\s*(?:[A-Za-zÅ°%µ]{1,3}(?![A-Za-z])|%)?|\S+/g;

/**
 * Cut each legend label down to what tells the runs apart. A conditions line runs to
 * a paragraph ("N = 288 atoms · seed = … · held at 1000 K"), and every run shares
 * nearly all of it, so as legend entries they buried the plot. Segments ("·"-separated)
 * that every run shares are dropped, then the words the remaining text shares at its
 * start and end, leaving "1000 K" vs "2000 K". The full line stays on the card above
 * the chart. Runs with nothing to tell them apart become "run 1", "run 2", ….
 */
export const compactLegendLabels = (labels: string[]): string[] => {
  if (labels.length < 2) return labels;
  const segs = labels.map((l) => l.split(/\s*·\s*/).filter(Boolean));
  const shared = segs[0].filter((s) => segs.every((ss) => ss.includes(s)));
  const texts = segs.map((ss) => ss.filter((s) => !shared.includes(s)).join(' · '));
  const toks = texts.map((t) => [...t.matchAll(LABEL_TOKEN)]);
  let pre = 0;
  while (toks.every((t) => pre < t.length && t[pre][0] === toks[0][pre][0])) pre++;
  let suf = 0;
  const tokAt = (t: RegExpMatchArray[], k: number) => t[t.length - 1 - k][0];
  while (toks.every((t) => suf < t.length - pre && tokAt(t, suf) === tokAt(toks[0], suf))) suf++;
  return toks.map((t, i) => {
    const kept = t.slice(pre, t.length - suf);
    // Slice the original text so punctuation keeps its spacing ("2000 K, then").
    const s = kept.length
      ? texts[i]
          .slice(kept[0].index, (kept[kept.length - 1].index ?? 0) + kept[kept.length - 1][0].length)
          .replace(/^[\s·,;:]+|[\s·,;:]+$/g, '')
      : '';
    return s || `run ${i + 1}`;
  });
};

/**
 * Legend labels for one chart: the earlier series (oldest first) followed by the
 * current run. Two runs under identical conditions — the common "repeat it" case —
 * would otherwise show two identical legend entries, which hides which curve is the
 * repeat. Labels that collide get a run number appended; distinct ones stay as
 * written. Done at render time, never stored, so a repeat of a repeat does not
 * compound into "… · run 2 · run 3".
 */
export const legendLabels = (labels: string[]): string[] => {
  const seen = new Map<string, number>();
  for (const l of labels) seen.set(l, (seen.get(l) ?? 0) + 1);
  return labels.map((l, i) => ((seen.get(l) ?? 0) > 1 ? `${l} · run ${i + 1}` : l));
};
